import { of } from 'rxjs';

import { TypedList } from '../../core/api/network.service';
import { DataSourceFunc } from '../../core/table/table-observable-data-source';
import { ID } from '../../models/id';
import {
  ChartOfAccountsFilters,
  ChartOfAccountsRow,
  PositionUpdate,
  SubcategoryFilterOption,
} from '../models/chart-of-accounts.models';
import { LedgerAccount, LedgerAccountUpdateResponse, LedgerSubcategoryOption } from '../models/ledger.models';

export function buildAccountRows(accounts: LedgerAccount[], filters: ChartOfAccountsFilters): ChartOfAccountsRow[] {
  const normalizedFilters = { ...filters, search: filters.search.trim().toLowerCase() };

  return accounts.flatMap((account) => buildSubtreeRows(account, normalizedFilters, 0));
}

export function getSubcategoryOptions(accounts: LedgerAccount[]): string[] {
  const subcategories = flattenAccounts(accounts)
    .map((account) => account.subcategory)
    .filter((subcategory): subcategory is string => !!subcategory);

  return [...new Set(subcategories)].sort((a, b) => a.localeCompare(b));
}

export function getSubcategoryLabel(subcategory: string | null, options: LedgerSubcategoryOption[]): string {
  return options.find((option) => option.value === subcategory)?.label ?? subcategory ?? '';
}

function getSubcategoryGroup(subcategory: string, options: LedgerSubcategoryOption[]): string {
  return options.find((option) => option.value === subcategory)?.financial_class_label.toUpperCase() ?? '';
}

export function getSubcategoryFilterOptions(
  accounts: LedgerAccount[],
  subcategories: LedgerSubcategoryOption[],
): SubcategoryFilterOption[] {
  return getSubcategoryOptions(accounts)
    .map((value) => ({
      value,
      label: getSubcategoryLabel(value, subcategories),
      group: getSubcategoryGroup(value, subcategories),
    }))
    .sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label));
}

export function searchSubcategoryOptions(options: SubcategoryFilterOption[]): DataSourceFunc<SubcategoryFilterOption> {
  return (params) => {
    const query = String(params?.search ?? '').toLowerCase();
    const matches = options.filter((option) => option.label.toLowerCase().includes(query));
    const pageSize = Number(params?.page_size ?? matches.length);
    return of({
      results: matches.slice(0, pageSize),
      count: matches.length,
    } satisfies TypedList<SubcategoryFilterOption>);
  };
}

export function getTopLevelAccountOptions(accounts: LedgerAccount[]): LedgerAccount[] {
  return accounts.filter((account) => !account.archived);
}

export function insertAccount(accounts: LedgerAccount[], account: LedgerAccount): LedgerAccount[] {
  if (account.parent_id === null) {
    return sortByPosition([...accounts, account]);
  }

  return accounts.map((existing) =>
    existing.id === account.parent_id
      ? { ...existing, children: sortByPosition([...existing.children, account]) }
      : { ...existing, children: insertAccount(existing.children, account) },
  );
}

export function removeAccount(accounts: LedgerAccount[], accountId: ID): LedgerAccount[] {
  return accounts
    .filter((existing) => existing.id !== accountId)
    .map((existing) => ({ ...existing, children: removeAccount(existing.children, accountId) }));
}

function sortByPosition(accounts: LedgerAccount[]): LedgerAccount[] {
  return [...accounts].sort((a, b) => a.position - b.position);
}

const NO_FILTERS: ChartOfAccountsFilters = { search: '', subcategory: null, archivedOnly: false };

function buildSubtreeRows(
  account: LedgerAccount,
  filters: ChartOfAccountsFilters,
  depth: number,
): ChartOfAccountsRow[] {
  const accountMatches = matchesFilters(account, filters);
  const childFilters = accountMatches ? NO_FILTERS : filters;
  const childRows = account.children.flatMap((child) => buildSubtreeRows(child, childFilters, depth + 1));
  const [firstChildRow, ...otherChildRows] = childRows;

  if (childRows.length === 0 && !accountMatches) {
    return [];
  }

  return [
    { account, depth, followsParent: false },
    ...(firstChildRow ? [{ ...firstChildRow, followsParent: true }, ...otherChildRows] : []),
  ];
}

function matchesFilters(account: LedgerAccount, filters: ChartOfAccountsFilters): boolean {
  return (
    (!filters.search || account.name.toLowerCase().includes(filters.search)) &&
    (!filters.subcategory || account.subcategory === filters.subcategory) &&
    (!filters.archivedOnly || account.archived)
  );
}

function flattenAccounts(accounts: LedgerAccount[]): LedgerAccount[] {
  return accounts.flatMap((account) => [account, ...flattenAccounts(account.children)]);
}

export function moveAccount(accounts: LedgerAccount[], movedId: ID, targetId: ID): LedgerAccount[] {
  const flat = flattenAccounts(accounts);
  const moved = flat.find((account) => account.id === movedId);
  const target = flat.find((account) => account.id === targetId);

  if (!moved || !target || moved.parent_id !== target.parent_id) {
    return accounts;
  }

  return reorderGroup(accounts, moved.parent_id, (group) => {
    const reordered = group.filter((account) => account.id !== movedId);
    reordered.splice(
      group.findIndex((account) => account.id === targetId),
      0,
      moved,
    );
    return reordered;
  });
}

export function buildPositionUpdates(original: LedgerAccount[], reordered: LedgerAccount[]): PositionUpdate[] {
  const originalGroups = groupByParent(flattenAccounts(original));

  return [...groupByParent(flattenAccounts(reordered))].flatMap(([parentId, target]) => {
    const group = originalGroups.get(parentId) ?? [];
    return planGroupMoves(group, target).map(({ account, index }) => ({
      id: account.id,
      position: group[index]?.position ?? account.position,
    }));
  });
}

export function applyAccountUpdate(accounts: LedgerAccount[], update: LedgerAccountUpdateResponse): LedgerAccount[] {
  const changed = new Map([update.account, ...update.renumbered].map((account) => [account.id, account]));
  const flat = flattenAccounts(accounts).map((account) => ({ ...account, ...changed.get(account.id) }));
  const ids = new Set(flat.map((account) => account.id));
  const groups = groupByParent(flat.filter((account) => account.parent_id === null || ids.has(account.parent_id)));
  const roots = flat.filter((account) => account.parent_id !== null && !ids.has(account.parent_id));
  const build = (group: LedgerAccount[]): LedgerAccount[] =>
    [...group]
      .sort((a, b) => a.position - b.position)
      .map((account) => ({ ...account, children: build(groups.get(account.id) ?? []) }));

  return build([...(groups.get(null) ?? []), ...roots]);
}

function groupByParent(accounts: LedgerAccount[]): Map<ID | null, LedgerAccount[]> {
  const groups = new Map<ID | null, LedgerAccount[]>();
  accounts.forEach((account) => groups.set(account.parent_id, [...(groups.get(account.parent_id) ?? []), account]));
  return groups;
}

function planGroupMoves(
  original: LedgerAccount[],
  target: LedgerAccount[],
): { account: LedgerAccount; index: number }[] {
  const moves: { account: LedgerAccount; index: number }[] = [];
  let current = original.filter((account) => target.some((t) => t.id === account.id));
  const mismatches = (order: LedgerAccount[]): number => order.filter((a, i) => a.id !== target[i]?.id).length;
  const relocate = (order: LedgerAccount[], account: LedgerAccount, index: number): LedgerAccount[] => {
    const rest = order.filter((a) => a.id !== account.id);
    return [...rest.slice(0, index), account, ...rest.slice(index)];
  };

  target.forEach((wanted, index) => {
    if (current[index]?.id === wanted.id) {
      return;
    }
    const displaced = current[index];
    const displacedIndex = displaced ? target.findIndex((a) => a.id === displaced.id) : -1;
    const pushed = displacedIndex >= 0 ? relocate(current, displaced, displacedIndex) : current;
    const pulled = relocate(current, wanted, index);
    const pushDown = pushed[index]?.id === wanted.id && mismatches(pushed) < mismatches(pulled);
    moves.push(pushDown ? { account: displaced, index: displacedIndex } : { account: wanted, index });
    current = pushDown ? pushed : pulled;
  });

  return moves;
}

function reorderGroup(
  accounts: LedgerAccount[],
  parentId: ID | null,
  reorder: (group: LedgerAccount[]) => LedgerAccount[],
): LedgerAccount[] {
  if (parentId === null) {
    return reorder(accounts);
  }

  return accounts.map((account) =>
    account.id === parentId
      ? { ...account, children: reorder(account.children) }
      : { ...account, children: reorderGroup(account.children, parentId, reorder) },
  );
}
